'use client';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import React, { useState, useCallback, useEffect, Children } from 'react';
import { ArrowControl } from '../../Controls/ArrowControl/ArrowControl.mjs';
import { CarouselCounter } from '../CarouselCounter/CarouselCounter.mjs';
import { factory } from '../../factory/factory.utils.mjs';
import { Box } from '../../Other/Box/Box.mjs';
import { clamp } from '../../utils/helpers.mjs';
import { CarouselProvider } from './Carousel.context.mjs';
import classes from './Carousel.module.css.mjs';

const Carousel = factory((props, ref) => {
  const {
    className,
    children,
    onNextSlide,
    onPreviousSlide,
    onSlideChange,
    orientation = "horizontal",
    slideSize = "100%",
    getEmblaApi,
    slideGap = 0,
    height = "auto",
    align = "center",
    slidesToScroll = 1,
    includeGapInSize = true,
    draggable = true,
    dragFree = false,
    loop = true,
    speed = 25,
    initialSlide = 0,
    inViewThreshold = 0,
    withControls = true,
    withIndicators = false,
    skipSnaps = false,
    containScroll,
    withKeyboardEvents = true,
    carouselControlProps,
    controlsOffset,
    customRightControl,
    customLeftControl,
    hideControlOnDisabled = true,
    ...rest
  } = props;
  const dir = "ltr";
  const [emblaRefElement, embla] = useEmblaCarousel({
    axis: orientation === "horizontal" ? "x" : "y",
    direction: orientation === "horizontal" ? dir : void 0,
    startIndex: initialSlide,
    loop,
    align,
    slidesToScroll,
    watchDrag: draggable,
    dragFree,
    duration: speed,
    inViewThreshold,
    skipSnaps,
    containScroll
  });
  const [selected, setSelected] = useState(0);
  const [slidesCount, setSlidesCount] = useState(0);
  const handleSelect = useCallback(() => {
    if (!embla) return;
    const slide = embla.selectedScrollSnap();
    setSelected(slide);
    onSlideChange?.(slide);
  }, [embla, setSelected]);
  const handlePrevious = useCallback(() => {
    embla?.scrollPrev();
    onPreviousSlide?.();
  }, [embla]);
  const handleNext = useCallback(() => {
    embla?.scrollNext();
    onNextSlide?.();
  }, [embla]);
  const handleKeydown = useCallback(
    (event) => {
      if (withKeyboardEvents) {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          handleNext();
        }
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          handlePrevious();
        }
      }
    },
    [embla]
  );
  useEffect(() => {
    if (embla) {
      getEmblaApi?.(embla);
      handleSelect();
      setSlidesCount(embla.scrollSnapList().length);
      embla.on("select", handleSelect);
      return () => {
        embla.off("select", handleSelect);
      };
    }
    return void 0;
  }, [embla, slidesToScroll]);
  useEffect(() => {
    if (embla) {
      embla.reInit();
      setSlidesCount(embla.scrollSnapList().length);
      setSelected((currentSelected) => clamp(currentSelected, 0, Children.toArray(children).length - 1));
    }
  }, [Children.toArray(children).length, slidesToScroll]);
  const canScrollPrev = embla?.canScrollPrev() || false;
  const canScrollNext = embla?.canScrollNext() || false;
  const showLeftControl = embla && (canScrollPrev || loop);
  const showRightControl = embla && (canScrollNext || loop);
  return /* @__PURE__ */ React.createElement(CarouselProvider, { value: { orientation } }, /* @__PURE__ */ React.createElement(
    Box,
    {
      ref,
      className: clsx(classes.root, className, "kohler-carousel-root"),
      ...rest,
      onKeyDownCapture: handleKeydown,
      style: {
        "--carousel-height": height,
        "--carousel-slide-size": slideSize || void 0,
        "--carousel-slide-gap": slideGap,
        "--carousel-controls-offset": controlsOffset || void 0
      },
      "data-include-gap-in-size": includeGapInSize || void 0
    },
    /* @__PURE__ */ React.createElement("div", { className: clsx(classes.viewport, "kohler-carousel-content"), ref: emblaRefElement }, /* @__PURE__ */ React.createElement("div", { className: clsx(classes.container, "kohler-carousel-container"), "data-orientation": orientation }, children)),
    withIndicators && /* @__PURE__ */ React.createElement("div", { className: clsx(classes.indicators, "kohler-carousel-indicators"), "data-orientation": orientation }, /* @__PURE__ */ React.createElement(CarouselCounter, { total: slidesCount, current: selected + 1 })),
    withControls && /* @__PURE__ */ React.createElement("div", { className: clsx(classes.controls, "kohler-carousel-controls"), "data-orientation": orientation }, customLeftControl ? React.isValidElement(customLeftControl) ? React.cloneElement(customLeftControl, {
      "data-inactive": !canScrollPrev || void 0,
      tabIndex: canScrollPrev ? 0 : -1,
      onClick: () => {
        handlePrevious();
      },
      className: clsx(
        classes.control,
        !showLeftControl && hideControlOnDisabled && classes.controlLeftHidden,
        customLeftControl.props?.className,
        carouselControlProps?.className
      ),
      ...carouselControlProps
    }) : customLeftControl : /* @__PURE__ */ React.createElement(
      ArrowControl,
      {
        variant: "carousel",
        direction: "left",
        "data-inactive": !canScrollPrev || void 0,
        tabIndex: canScrollPrev ? 0 : -1,
        onClick: handlePrevious,
        disabled: !hideControlOnDisabled && !canScrollPrev,
        ...carouselControlProps,
        className: clsx(
          classes.control,
          !showLeftControl && hideControlOnDisabled && classes.controlLeftHidden,
          carouselControlProps?.className
        )
      }
    ), customRightControl ? React.isValidElement(customRightControl) ? React.cloneElement(customRightControl, {
      "data-inactive": !canScrollNext || void 0,
      tabIndex: canScrollNext ? 0 : -1,
      onClick: () => {
        handleNext();
      },
      className: clsx(
        classes.control,
        !showRightControl && hideControlOnDisabled && classes.controlRightHidden,
        customRightControl.props?.className,
        carouselControlProps?.className
      ),
      ...carouselControlProps
    }) : customRightControl : /* @__PURE__ */ React.createElement(
      ArrowControl,
      {
        variant: "carousel",
        direction: "right",
        "data-inactive": !canScrollNext || void 0,
        tabIndex: canScrollNext ? 0 : -1,
        onClick: handleNext,
        disabled: !hideControlOnDisabled && !canScrollNext,
        ...carouselControlProps,
        className: clsx(
          classes.control,
          !showRightControl && hideControlOnDisabled && classes.controlRightHidden,
          carouselControlProps?.className
        )
      }
    ))
  ));
});

export { Carousel };
